import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { mget, mpost, mput, errMsg, eur } from "@/manager/lib";
import { PageHeader, Card, Field, Pill, Empty, goldBtn } from "@/manager/ui";

const BLANK = { name: "", phone: "", role: "Camarero/a", cost_per_hour: "", cost_per_service: "", notes: "", active: true };
const num = (v) => (v === "" || v == null ? null : Number(v));

export default function Staff() {
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => mget("/staff").then(setList), []);
  useEffect(() => { load(); }, [load]);
  const save = async (e) => {
    e.preventDefault();
    const body = { ...edit, cost_per_hour: num(edit.cost_per_hour), cost_per_service: num(edit.cost_per_service) };
    try { if (edit.id) await mput(`/staff/${edit.id}`, body); else await mpost("/staff", body); toast.success("Guardado"); setEdit(null); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const set = (k) => (e) => setEdit({ ...edit, [k]: e.target.value });
  return (
    <div data-testid="manager-staff">
      <PageHeader eyebrow="Personal" title="Camareros y colaboradores">
        <Button className={goldBtn} onClick={() => setEdit(BLANK)} data-testid="staff-new"><Plus size={15} /> Nueva persona</Button>
      </PageHeader>
      <p className="text-sm text-[#6b6a66] mb-4">La regla de camareros recomendados (por número de invitados) se cambia en Configuración. En cada evento puedes poner 1, 2 o los que necesites.</p>
      <Card className="divide-y divide-[#f0ece4]">
        {list.map((s) => (
          <div key={s.id} className="flex flex-wrap items-center gap-3 px-5 py-3" data-testid={`staff-row-${s.id}`}>
            <div className="flex-1"><p className="font-medium text-sm">{s.name}</p><p className="text-xs text-[#6b6a66]">{s.role} · {s.phone || "sin teléfono"}</p></div>
            <span className="text-xs text-[#6b6a66]">{eur(s.cost_per_hour)}/h · {eur(s.cost_per_service)}/servicio</span>
            <Pill className={s.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500"}>{s.active ? "Activo" : "Inactivo"}</Pill>
            {s.phone && <a href={`tel:${s.phone}`} className="text-xs underline">Llamar</a>}
            <button onClick={() => setEdit({ ...s, cost_per_hour: s.cost_per_hour ?? "", cost_per_service: s.cost_per_service ?? "" })} data-testid={`staff-edit-${s.id}`}><Pencil size={15} /></button>
          </div>
        ))}
        {!list.length && <Empty>Aún no hay personal. Añade tu equipo o carga los datos de ejemplo en Configuración.</Empty>}
      </Card>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="bg-white" data-testid="staff-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{edit?.id ? "Editar persona" : "Nueva persona"}</DialogTitle></DialogHeader>
          {edit && (
            <form onSubmit={save} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Nombre *"><Input value={edit.name} onChange={set("name")} required data-testid="staff-name" /></Field>
                <Field label="Teléfono"><Input value={edit.phone} onChange={set("phone")} data-testid="staff-phone" /></Field>
                <Field label="Función"><Input value={edit.role} onChange={set("role")} data-testid="staff-role" /></Field>
                <Field label="Coste por servicio (€)"><Input type="number" step="0.01" value={edit.cost_per_service} onChange={set("cost_per_service")} data-testid="staff-cost-service" /></Field>
                <Field label="Coste por hora (€)"><Input type="number" step="0.01" value={edit.cost_per_hour} onChange={set("cost_per_hour")} data-testid="staff-cost-hour" /></Field>
                <label className="flex items-center gap-2 text-sm pt-6"><Switch checked={edit.active} onCheckedChange={(v) => setEdit({ ...edit, active: v })} data-testid="staff-active" /> Activo</label>
              </div>
              <Field label="Observaciones"><Input value={edit.notes} onChange={set("notes")} data-testid="staff-notes" /></Field>
              <DialogFooter><Button type="submit" className={goldBtn} data-testid="staff-save">Guardar</Button></DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
